import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * Mobile header with consistent back navigation.
 *
 * TT-R145-02: "Back consistently returns one step to the previous screen."
 * TT-R128-03: back from a More sub-section returns to More, not the dashboard.
 * TT-R145-03: the return control is labelled "Back" and never "More", so the
 * navigation destination "More" and the "get more info" action can never be
 * confused. The optional trailing action is therefore labelled explicitly by
 * the caller (for example "About this widget"), never "More".
 */

export type MobileHeaderProps = {
  title: string;
  /**
   * Explicit fallback when there is no history entry to pop — for example a
   * deep link opened directly. Guarantees back never dumps the user on Home
   * when they expected the previous screen.
   */
  backTo?: string;
  /** Label describing where back goes, announced to screen readers. */
  backLabel?: string;
  trailing?: ReactNode;
  /** Suppresses the back control on top-level tabs. */
  root?: boolean;
};

export function MobileHeader({
  title,
  backTo,
  backLabel,
  trailing,
  root = false,
}: MobileHeaderProps) {
  const navigate = useNavigate();

  const goBack = () => {
    // Prefer the explicit destination so the target is always predictable.
    if (backTo) {
      navigate(backTo);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/mobile/home');
  };

  return (
    <header className="tm-header">
      <div className="tm-header__lead">
        {root ? null : (
          <button
            type="button"
            className="tm-icon-btn"
            onClick={goBack}
            aria-label={backLabel ? `Back to ${backLabel}` : 'Back'}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
      </div>
      <h1 className="tm-header__title">{title}</h1>
      <div className="tm-header__trail">{trailing}</div>
    </header>
  );
}
